import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Select } from '../ds/index'

export type LeadSort = 'best' | 'newest' | 'oldest' | 'az' | 'za'

const LABELS: Record<LeadSort, string> = {
  best: 'Best first', newest: 'Newest first', oldest: 'Oldest first', az: 'A to Z', za: 'Z to A',
}

/**
 * The order a lead list is in, kept in the address beside the filters so a
 * reload or a back link returns to the same order. The server sorts, so a
 * capped list holds the first rows in this order, not the newest rows reshuffled.
 */
export function useSortParam(choices: LeadSort[], fallback: LeadSort) {
  const [params, setParams] = useSearchParams()
  const raw = params.get('sort') as LeadSort | null
  const sort = raw && choices.includes(raw) ? raw : fallback
  const setSort = (v: LeadSort) => setParams((p) => {
    const n = new URLSearchParams(p)
    if (v === fallback) n.delete('sort'); else n.set('sort', v)
    return n
  }, { replace: true })
  return [sort, setSort] as const
}

/**
 * Puts back the order and filters a person last used on a list when they come
 * back to it with none in the address (the sidebar, the back link on a lead,
 * the app reopening after a trip to Facebook), and saves every change after.
 * Kept in this browser per signed-in person; a private window starts fresh.
 * The first pass only restores, so the empty address never overwrites what was saved.
 */
export function useRememberedList(key: string | null, names: string[]) {
  const [params, setParams] = useSearchParams()
  const restored = useRef<string | null>(null)
  useEffect(() => {
    if (!key) return
    if (restored.current !== key) {
      restored.current = key
      if (!names.some((n) => params.has(n))) {
        let saved = ''
        try { saved = localStorage.getItem(key) || '' } catch { /* storage blocked */ }
        const from = new URLSearchParams(saved)
        if (names.some((n) => from.get(n))) {
          setParams((p) => {
            const next = new URLSearchParams(p)
            for (const n of names) { const v = from.get(n); if (v) next.set(n, v) }
            return next
          }, { replace: true })
          return
        }
      }
    }
    const out = new URLSearchParams()
    for (const n of names) { const v = params.get(n); if (v) out.set(n, v) }
    try { if (out.toString()) localStorage.setItem(key, out.toString()); else localStorage.removeItem(key) } catch { /* storage blocked */ }
  }, [key, params]) // eslint-disable-line react-hooks/exhaustive-deps
}

/** A to Z goes by the person's name where one is known, then by what they asked for. */
export function SortSelect({ value, onChange, choices }: { value: LeadSort; onChange: (v: LeadSort) => void; choices: LeadSort[] }) {
  return (
    <Select aria-label="Order" value={value} onChange={(e) => onChange(e.target.value as LeadSort)}
            title="A to Z goes by the person's name, then by what they asked for">
      {choices.map((c) => <option key={c} value={c}>{LABELS[c]}</option>)}
    </Select>
  )
}

/** "Maria G." from "Maria Gonzalez": the short form lists use. */
export function shortName(name: string | null | undefined) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return ''
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0]
}
